import { computed } from 'vue';
import type { Ref } from 'vue';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { navActiveStatesUpdate, type NavMenuSection } from '../../../cross-app-frontend/navigation';
import { facilityFromRouteOrLastVisited, yearFromRouteOrLastVisited } from '@/router';
import { slugFromName } from '../../../tri-shared/services/formatting';
import { facilityPermissions } from '@/services/navigation/navigation-permissions';

export function useHazWasteNavMenu(currentRoute: Ref<RouteLocationNormalizedLoadedGeneric>): Ref<NavMenuSection[]> {
  return computed((): NavMenuSection[] => {
    const facility = facilityFromRouteOrLastVisited.value;
    if (!facility) return [];

    const facilityKey = facility.facilityID;
    const perms = facilityPermissions.value[facilityKey];
    const hasAdministerFacility = perms?.hasAdministerFacility ?? false;
    const hasManageUsers = perms?.hasManageUsers ?? false;
    const isAdmin = hasAdministerFacility;

    const facilityParams = {
      facilityID: facility.facilityID,
      facilitySlug: slugFromName(facility.name),
    };

    const facilityParamsWithYear = {
      ...facilityParams,
      year: yearFromRouteOrLastVisited.value,
    };

    const result: NavMenuSection[] = [
      {
        id: 'workflow',
        title: 'Hazardous Waste',
        groups: [
          {
            title: 'General',
            children: [
              {
                title: 'Overview',
                icon: 'fa-building',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Overview', params: facilityParams },
              },
              {
                title: 'Tasks',
                icon: 'fa-list-check',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Tasks', params: facilityParams },
              },
            ],
          },
          {
            title: 'Waste Management',
            children: [
              {
                title: 'Waste Streams',
                icon: 'fa-flask-round-potion',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Waste Streams', params: facilityParams },
              },
              {
                title: 'Generation Log',
                icon: 'fa-calendar-days',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Generation Log', params: facilityParamsWithYear },
              },
              {
                title: 'Accumulation Areas',
                icon: 'fa-warehouse',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Accumulation Areas', params: facilityParams },
              },
              {
                title: 'Inspections',
                icon: 'fa-clipboard-check',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Inspections', params: facilityParams },
              },
            ],
          },
          {
            title: 'Shipments',
            children: [
              {
                title: 'RCRA Shipments',
                icon: 'fa-file-signature',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Manifests', params: facilityParams },
              },
              {
                title: 'Other Shipments',
                icon: 'fa-truck',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Other Shipments', params: facilityParams },
              },
            ],
          },
          {
            title: 'Biennial Reporting',
            children: [
              {
                title: 'Biennial Report',
                icon: 'fa-file-export',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Biennial Report', params: facilityParamsWithYear },
              },
            ],
          },
          {
            title: 'Emergency Response',
            children: [
              {
                title: 'Site Map',
                icon: 'fa-map',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Site Map', params: facilityParams },
              },
              {
                title: 'Quick Reference Guide',
                icon: 'fa-book-medical',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Quick Reference Guide', params: facilityParams },
              },
            ],
          },
          {
            title: 'Training',
            children: [
              {
                title: 'Profile',
                icon: 'fa-clipboard-user',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Training Profile', params: facilityParams },
              },
              {
                title: 'Configuration',
                icon: 'fa-sliders',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Training Configuration', params: facilityParams },
              },
              {
                title: 'Help & tours',
                icon: 'fa-circle-question',
                type: 'link',
                isActive: false,
                to: { name: 'HazWaste Help', params: facilityParams },
              },
            ],
          },
        ],
      },
      ...(isAdmin || hasManageUsers
        ? [
            {
              id: 'admin',
              title: 'Admin',
              groups: [
                {
                  title: 'Admin',
                  children: [
                    {
                      title: 'Facilities',
                      icon: 'fa-city',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Facilities' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Users',
                      icon: 'fa-users',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Users' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Training Management',
                      icon: 'fa-user-graduate',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'HazWaste Training Management' as const, params: facilityParams },
                    },
                  ],
                },
              ],
            } as NavMenuSection,
          ]
        : []),
    ];

    navActiveStatesUpdate(currentRoute.value, result);
    if (
      ['HazWaste Waste Streams', 'HazWaste Waste Stream New', 'HazWaste Waste Stream Detail'].includes(
        String(currentRoute.value.name),
      )
    ) {
      const wasteStreamsItem = result
        .flatMap((section) => section.groups)
        .flatMap((group) => group.children)
        .find((child) => child.type === 'link' && child.title === 'Waste Streams');
      if (wasteStreamsItem) wasteStreamsItem.isActive = true;
    }
    return result;
  });
}
